/**
 * Render scheduler (RFC §3a, NORMATIVE) — the element-level dedupe layer.
 *
 * A change to any dep marks its element dirty in a module-level set; the first
 * mark schedules ONE flush; the flush calls `flushRender()` on each dirty target
 * once, in DOM-document order, then clears. Re-entrant marks made *during* a flush
 * land in the NEXT tick's set (they don't extend the current flush).
 *
 * This is the second half of the two-layer coalescing contract: `batch()` (see
 * signal.ts) collapses synchronous writes so no subscriber observes an intermediate
 * value; the scheduler collapses N dep-notifications per element into ONE render.
 * Neither substitutes for the other.
 *
 * Flush policy is per-target opt-in: "microtask" (default) or "raf". `raf` uses
 * `requestAnimationFrame` when available and falls back to a microtask when not
 * (e.g. under `bun:test`, where there is no `requestAnimationFrame`).
 *
 * Designed to be unit-testable WITHOUT a DOM: document ordering uses
 * `Node.compareDocumentPosition` when both targets are Nodes, and otherwise
 * preserves insertion order (stable). Tests may inject a comparator and a fake
 * `requestAnimationFrame` via the testing seams at the bottom of this module.
 */

/** A render target — implemented by `KitElement` in a later slice. */
export interface RenderTarget {
  /** Render this target once. Called by the scheduler during a flush. */
  flushRender(): void;
  /** Flush lane for this target. Defaults to "microtask". */
  readonly flushPolicy?: FlushPolicy;
}

export type FlushPolicy = "microtask" | "raf";

/** Orders two dirty targets within a lane. Negative = `a` before `b`. */
export type TargetComparator = (a: RenderTarget, b: RenderTarget) => number;

// ── Module state ────────────────────────────────────────────────────────────
const dirty = new Set<RenderTarget>();
let microtaskScheduled = false;
let rafScheduled = false;
let waiters: Array<() => void> = [];

// ── Injectable seams (defaults wired to the ambient environment) ─────────────
let rafImpl: ((cb: () => void) => unknown) | null =
  typeof requestAnimationFrame === "function"
    ? (cb) => requestAnimationFrame(() => cb())
    : null;
let comparator: TargetComparator = defaultCompare;

/** Effective lane for a target: "raf" only when requested AND raf is available. */
function laneOf(target: RenderTarget): FlushPolicy {
  return target.flushPolicy === "raf" && rafImpl ? "raf" : "microtask";
}

function defaultCompare(a: RenderTarget, b: RenderTarget): number {
  const an = a as unknown as Node;
  const bn = b as unknown as Node;
  if (isNode(an) && isNode(bn)) {
    if (an === bn) return 0;
    const pos = an.compareDocumentPosition(bn);
    // DOCUMENT_POSITION_FOLLOWING (4): b follows a → a comes first.
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  }
  // Non-Node targets: preserve insertion order (Array.sort is stable).
  return 0;
}

function isNode(x: unknown): x is Node {
  return (
    typeof Node !== "undefined" &&
    x instanceof Node &&
    typeof (x as Node).compareDocumentPosition === "function"
  );
}

// ── Runaway guard ────────────────────────────────────────────────────────────
// A target that marks itself dirty during EVERY flush produces an unbroken
// queueMicrotask chain: microtasks starve the event loop and the renderer
// never yields — a SILENT freeze. The guard makes that failure diagnosable and
// survivable: after RUNAWAY_CHAIN_LIMIT consecutive re-entrant flushes without
// a macrotask yield, it console.errors ONCE per episode (naming the offending
// targets' tags) and routes further flushes through setTimeout(0) — the app
// degrades to macrotask-rate rendering instead of hanging. Always on: a broken
// chain plus an error beats a frozen tab in every environment.
const RUNAWAY_CHAIN_LIMIT = 1000;
let flushing = false;
let chainLength = 0;
let guardTripped = false;

const describeTargets = (): string => {
  const names = new Set<string>();
  for (const t of dirty) {
    const el = t as unknown as { localName?: string };
    names.add(el.localName ?? t.constructor?.name ?? "unknown target");
  }
  return [...names].join(", ") || "(none)";
};

function scheduleLane(lane: FlushPolicy): void {
  if (lane === "raf" && rafImpl) {
    if (!rafScheduled) {
      rafScheduled = true;
      rafImpl(() => flushLane("raf"));
    }
  } else {
    if (!microtaskScheduled) {
      microtaskScheduled = true;
      if (flushing) {
        // Re-entrant mark: this flush was scheduled from within a flush.
        chainLength++;
        if (chainLength > RUNAWAY_CHAIN_LIMIT) {
          if (!guardTripped) {
            guardTripped = true;
            console.error(
              `[web-kit] scheduler runaway: ${RUNAWAY_CHAIN_LIMIT}+ consecutive ` +
                `re-entrant flushes without yielding to the event loop — a render ` +
                `target keeps marking itself dirty during flushRender(). ` +
                `Offending target(s): ${describeTargets()}. Breaking the microtask ` +
                `chain via setTimeout(0); rendering continues at macrotask rate.`
            );
          }
          setTimeout(() => flushLane("microtask"), 0); // yield to the event loop
          return;
        }
      } else {
        chainLength = 0; // fresh mark from a task — a new episode
      }
      queueMicrotask(() => flushLane("microtask"));
    }
  }
}

function flushLane(lane: FlushPolicy): void {
  // Reset the scheduled flag FIRST, so re-entrant marks during flushRender()
  // schedule the NEXT tick instead of joining this one.
  if (lane === "raf") rafScheduled = false;
  else microtaskScheduled = false;

  const targets: RenderTarget[] = [];
  for (const t of dirty) {
    if (laneOf(t) === lane) targets.push(t);
  }
  for (const t of targets) dirty.delete(t);
  targets.sort(comparator);

  flushing = true;
  try {
    for (const t of targets) t.flushRender();
  } finally {
    flushing = false;
  }

  if (!microtaskScheduled && !rafScheduled) {
    chainLength = 0; // chain episode over — the scheduler yielded
    guardTripped = false;
  }
  maybeResolveWaiters();
}

function maybeResolveWaiters(): void {
  if (
    dirty.size === 0 &&
    !microtaskScheduled &&
    !rafScheduled &&
    waiters.length > 0
  ) {
    const pending = waiters;
    waiters = [];
    for (const resolve of pending) resolve();
  }
}

/**
 * Mark a target dirty. The first mark (per lane) schedules exactly one flush;
 * subsequent marks before that flush coalesce (the target renders once).
 */
export function markDirty(target: RenderTarget): void {
  dirty.add(target);
  scheduleLane(laneOf(target));
}

/**
 * Resolve after the pending flush completes (and the scheduler is fully idle),
 * or immediately if already clean. Cascading re-entrant marks keep it pending
 * until the queue drains.
 */
export function flushed(): Promise<void> {
  if (dirty.size === 0 && !microtaskScheduled && !rafScheduled) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => waiters.push(resolve));
}

// ── Testing seams (only resetSchedulerForTesting rides the public index —
//    downstream suites need it and the exports map blocks subpath imports) ────

/** TEST ONLY: inject a deterministic ordering comparator. */
export function setFlushComparator(cmp: TargetComparator | null): void {
  comparator = cmp ?? defaultCompare;
}

/** TEST ONLY: inject (or disable, with `null`) a fake `requestAnimationFrame`. */
export function setRafImpl(fn: ((cb: () => void) => unknown) | null): void {
  rafImpl = fn;
}

/** TEST ONLY: reset all scheduler module state. */
export function resetSchedulerForTesting(): void {
  dirty.clear();
  microtaskScheduled = false;
  rafScheduled = false;
  waiters = [];
  flushing = false;
  chainLength = 0;
  guardTripped = false;
  comparator = defaultCompare;
  rafImpl =
    typeof requestAnimationFrame === "function"
      ? (cb) => requestAnimationFrame(() => cb())
      : null;
}
